// src/encounter/ap-search-rail.tsx
//
// THE A/P LEFT RAIL — the CONTAINER over the Signal DS `ApSearchRail` organism.
//
// The rail's whole anatomy (tool strip, search + Suggest, FAVOURITE <CATEGORY> with the ICD-10 /
// Bundles sub-tab, SUGGESTED DIAGNOSIS, GLOBAL) graduated into Signal DS from this file (DS-D,
// Ahmed 2026-09-23 — superseding Andrew 2026-08-06 "match the Claude Design exactly and ignore Signal
// DS constraints", which is why it was hand-built here; the DS header quotes that ruling verbatim).
// Everything visual now renders from the DS. What stays here is what feeds a query:
//   · the controlled state — active category, query, `showGlobal`, and the `aiAsked` Suggest latch;
//   · the three search hooks (`useApCategorySearch`, `useAiDiagnosisSearch`, `useArticleSearch`),
//     the 600ms education debounce (Flutter SimpleSearchWidget) and the favourites-first AUTO-WIDEN
//     (assessment_plan_search_list_widget.dart:681-688);
//   · the row mappers (education articles, AI suggestions) and the `onAdd` routing.
// The latch resets that used to live in this file's handlers now fire from the DS handlers as
// callbacks: a segment switch calls `onQuery('')`, `onSuggest(false)` and `onShowGlobal(false)` — so
// the old `useEffect(() => setShowGlobal(false), [activeCategory])` is gone — and a typed or cleared
// query calls `onSuggest(false)`.
//
// DRAG SOURCES stay the PROD-1588 contract: every row keeps the RAW app row in `item`, and the DS
// writes `row.item ?? row` into the AP_DRAG_MIME payload.
//
// `Bundles` (PROD-1839, `GET /api/Patients/Bundle/{providerId}`) is fetched by the HOST and passed
// in — the id that route needs is `user.id`, not this component's `providerId` prop, and conflating
// the two silently returns an empty list.
//
// ARCHIVE CONFIRM (PROD-2697). The DS row's bin button no longer archives on the spot: it opens the
// "Archive bundle?" confirm here, and only Archive calls the host's `onDeleteBundle`. The confirm
// lives in THIS container, not the page, because the container is what mounts inside the Add-Order
// drawer as well as the A/P rail — so the dialog sits in the drawer's own tree when opened from it.
import { useEffect, useState } from 'react';
import {
  ApSearchRail,
  type ApSearchCategory,
  type ApSearchItem,
  type ApSearchRailHostSubTab,
  type ApSearchRailRow,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { useApCategorySearch } from './services/use-ap-category-search';
import { useDesignV2 } from './lib/design-v2';
import { useAiDiagnosisSearch } from './services/use-ai-diagnosis-search';
import { useArticleSearch } from './services/use-article-search';
import type { EducationArticle } from './lib/to-education-article';
import type { ApBundle } from './services/use-bundles';
import { ArchiveBundleConfirmDialog } from './archive-bundle-confirm-dialog';
import { OrderSetsRailSection, type OrderSetsRailSectionProps } from './order-sets/order-sets-rail-list';

/** Flutter SimpleSearchWidget debounce for the education (GetArticles) search. */
const EDUCATION_DEBOUNCE_MS = 600;

export interface ApSearchRailContainerProps {
  /** Tabs to show — permission DATA from the app (diagnosis first, then the allowed order types). */
  categories: ApSearchCategory[];
  providerId?: string;
  /**
   * Opens the rail on a given category with the search box pre-filled (PROD-1972).
   *
   * Applied at mount only — see the initialisers in the component body. Used by the ambient
   * accept path: when a proposal's ICD code does not resolve to exactly one Master concept,
   * the clinician gets a search already scoped to the right catalog and query rather than an
   * empty box and the proposal's wording to retype.
   */
  seed?: { category: ApSearchCategory; query: string };
  /**
   * Click / keyboard activation — the a11y path parallel to drag. With `frameV2` ON a third
   * argument, `query`, is the search text the rows on screen ANSWER at the moment of the pick, or ''
   * while results are still loading (the rows may then be the previous query's placeholder). The
   * Add-Order drawer reads it to tell a row from the unedited ambient-seeded search from one the
   * clinician found by retyping (`dictated-dose-for-pick.ts`). OFF: called with two arguments, as
   * before.
   */
  onActivateItem: (item: ApSearchItem, category: ApSearchCategory, query?: string) => void;
  onAddArticle?: (article: EducationArticle) => void;
  /** Tenant gate for the AI Suggest affordance — config DATA, never a tenant name checked here. */
  aiDiagnosisEnabled?: boolean;
  /**
   * The provider's saved bundles, fetched by the HOST (`useBundles`). Passed in rather than
   * fetched here because the id it needs is `user.id`, which is NOT this component's `providerId`
   * prop — see use-bundles.ts for why those two differ and what happens when you confuse them.
   */
  bundles?: ApBundle[];
  bundlesLoading?: boolean;
  /** Apply a bundle's saved A&P to the canvas. Absent → the row's add button is not rendered. */
  onImportBundle?: (bundle: ApBundle) => void;
  /**
   * Archive a bundle (POST /Bundle with isDeleted). Called only AFTER the clinician confirms the
   * "Archive bundle?" dialog. Absent → no archive affordance on the row.
   */
  onDeleteBundle?: (bundle: ApBundle) => void;
  /** True while an archive is in flight — disables every row's archive affordance. */
  deletingBundle?: boolean;
  /**
   * Whether the favourites header offers the Bundles tab. The live app shows it on the Add-Order
   * drawer (FAVOURITE DIAGNOSIS / ICD-10 / Bundles) but NOT on the Create-Bundle drawer, which is
   * ICD-10 only - you do not pick a bundle while authoring one. Screenshots, Andrew 2026-08-08.
   */
  showBundlesTab?: boolean;
  /**
   * ORDER SETS (PROD-2712), present only with the `VITE_ORDER_SETS_APPLY` flag on AND a user who
   * may order. Absent → the rail renders exactly as before (ICD-10 | Bundles). Present → the
   * sub-tab beside ICD-10 becomes "Order sets" (board 5): the DS `hostSubTab` relabels it, reads
   * "Your order sets" above it, and renders the caller's sets in place of the Bundles list.
   */
  orderSets?: Omit<OrderSetsRailSectionProps, 'query'>;
}

/**
 * The LEFT catalog rail of the A/P workspace. Owns its active category + query and runs the
 * category search hooks, so those hooks mount only where the rail does (an editable A/P) — a
 * read-only A/P still needs no QueryClient.
 */
export function ApSearchRailContainer({
  categories,
  providerId,
  onActivateItem,
  onAddArticle,
  aiDiagnosisEnabled,
  bundles = [],
  bundlesLoading = false,
  onImportBundle,
  onDeleteBundle,
  deletingBundle = false,
  showBundlesTab = true,
  seed,
  orderSets,
}: ApSearchRailContainerProps) {
  // Diagnosis is the FIRST tab (Flutter app_enum.dart:51-61) — default to it.
  //
  // PROD-1972: `seed` pre-fills the category and query via useState INITIALISERS, not an effect.
  // The rail is mounted by a drawer that is unmounted while closed, so each open is a fresh mount
  // and the initialiser runs exactly once per open. If the rail is ever kept mounted across opens,
  // this becomes wrong and needs a key or an explicit reset; a test asserts the seeded open.
  const [activeCategory, setActiveCategory] = useState<ApSearchCategory>(
    seed?.category ?? categories[0] ?? 'lab',
  );
  const [query, setQuery] = useState(seed?.query ?? '');
  /** `frameV2`: hand the settled query over with each pick (see `onActivateItem`). Read once per mount. */
  const reportsQuery = useDesignV2();

  const isEducation = activeCategory === 'education';
  const isDiagnosis = activeCategory === 'diagnosis';

  /**
   * AI SUGGESTED DIAGNOSIS (PROD-1801 / A/P-25) — fetched only when the clinician TAPS Suggest
   * (Flutter `getAIDiagnosisList`, assessment_plan_search_list_widget.dart:872-900, 1294-1320).
   * `aiAsked` is the "has it been asked for THIS text" latch; the DS resets it through `onSuggest`.
   */
  const [aiAsked, setAiAsked] = useState(false);

  /** The bundle whose archive is awaiting confirmation (PROD-2697); null → no dialog. */
  const [pendingArchive, setPendingArchive] = useState<ApBundle | null>(null);
  const aiSuggestions = useAiDiagnosisSearch(query, !!aiDiagnosisEnabled && isDiagnosis && aiAsked);

  /**
   * FAVOURITES FIRST, GLOBAL ON REQUEST (PROD-1801 / A/P-17). Reset on every category switch —
   * the DS segment handler calls `onShowGlobal(false)` (Flutter remounts the subtree per tab).
   */
  const [showGlobal, setShowGlobal] = useState(false);

  // Education has its own search (GetArticles) — an empty query keeps useApCategorySearch idle.
  const { items, loading } = useApCategorySearch(
    providerId,
    activeCategory,
    isEducation ? '' : query,
    showGlobal,
    // PROD-2931: Labs searches the clinic's tests and panels (order search) when it answers here.
    { labOrderSearch: true },
  );

  /**
   * AUTO-WIDEN when there is a query but no matching favourite — Flutter does exactly this
   * (assessment_plan_search_list_widget.dart:681-688). Without it the rail is a dead end for any
   * provider whose favourites are empty, which the source notes is common.
   */
  useEffect(() => {
    if (isEducation || showGlobal) return;
    if (!query.trim()) return;
    if (loading) return;
    if ((items.favourites ?? []).length === 0) setShowGlobal(true);
  }, [isEducation, showGlobal, query, loading, items.favourites]);

  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), EDUCATION_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);
  const eduSearch = useArticleSearch(debounced, isEducation && !!onAddArticle);

  /**
   * The rows under FAVOURITE <CATEGORY>. `item` must be the RAW row, never the ApSearchItem wrapper
   * — PROD-1588: the wrapper carries `.label` and no `.display`, which rendered blank seeded dialogs
   * and crashed `toLabOrder` on `result.display.trim()`.
   */
  const favourites: ApSearchRailRow[] = isEducation
    ? (eduSearch.data ?? []).flatMap((g) =>
        (g.articles ?? [])
          .filter((a) => a.display)
          .map((a) => ({
            id: a.id ?? (a.display as string),
            label: a.display as string,
            ...(a.articleUrl ? { previewUrl: a.articleUrl } : {}),
            item: { display: a.display as string, articleUrl: a.articleUrl, summary: a.summary } satisfies EducationArticle,
          })),
      )
    : (items.favourites ?? []);
  const globalRows: ApSearchRailRow[] = isEducation ? [] : items.results;

  /** Suggested rows in the `codeTitlePercentage` variant (ap_item_view_cell.dart:109-181). */
  const suggestionRows: ApSearchRailRow[] = (aiSuggestions.data ?? []).map((sug) => ({
    id: sug.id,
    code: sug.code,
    label: sug.display,
    // Flutter's tooltip on the term is the ICD `definition` — same text, native title.
    ...(sug.definition ? { title: sug.definition } : {}),
    ...(sug.percentageScore != null ? { score: sug.percentageScore } : {}),
    item: { id: sug.id, code: sug.code, display: sug.display, system: 'ICD-10' } satisfies CodeSearchResult,
  }));

  /** ＋ routing — education is Dx-less and goes to the article path; everything else activates. */
  function handleAdd(row: ApSearchRailRow, category: ApSearchCategory) {
    if (category === 'education') {
      onAddArticle?.(row.item as EducationArticle);
      return;
    }
    if (reportsQuery) onActivateItem(row, category, loading ? '' : query);
    else onActivateItem(row, category);
  }

  // ORDER SETS (PROD-2712, board 5): the rail's own ICD-10 | Order sets toggle, via the DS host
  // sub-tab (Signal DS 2.13.0). GLOBAL and Suggest hide on it; the list filters by the same field.
  const hostSubTab: ApSearchRailHostSubTab | undefined = orderSets
    ? {
        label: 'Order sets',
        eyebrow: 'Your order sets',
        render: (q) => <OrderSetsRailSection {...orderSets} query={q} />,
      }
    : undefined;

  return (
    <>
      <ApSearchRail<ApBundle>
        categories={categories}
        segment={activeCategory}
        onSegment={setActiveCategory}
        query={query}
        onQuery={setQuery}
        results={{ favourites, global: globalRows }}
        loading={isEducation ? eduSearch.isFetching : loading}
        showGlobal={showGlobal}
        onShowGlobal={setShowGlobal}
        onAdd={handleAdd}
        // Config DATA; the DS itself confines Suggest to the diagnosis tab, off the Bundles sub-tab.
        suggestEnabled={!!aiDiagnosisEnabled}
        suggestions={{
          asked: aiAsked,
          loading: aiSuggestions.isFetching,
          error: aiSuggestions.isError,
          rows: suggestionRows,
        }}
        onSuggest={setAiAsked}
        bundles={bundles}
        bundlesLoading={bundlesLoading}
        onImportBundle={onImportBundle}
        onDeleteBundle={onDeleteBundle ? setPendingArchive : undefined}
        deletingBundle={deletingBundle}
        showBundlesTab={showBundlesTab}
        hostSubTab={hostSubTab}
      />
      <ArchiveBundleConfirmDialog
        bundle={pendingArchive}
        onCancel={() => setPendingArchive(null)}
        onConfirm={(bundle) => {
          setPendingArchive(null);
          onDeleteBundle?.(bundle);
        }}
      />
    </>
  );
}
